'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArtigoLista } from '@/lib/types';
import { BannerMoreArticles } from '../ui/BannerMoreArticles';

let ARTICLES_CACHE: { data: ArtigoLista[]; timestamp: number } | null = null;
const ARTICLES_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutos

const AREAS = [
  {
    id: '0',
    color: '#3C6E71',
    title: 'Direito Cível',
  },
  {
    id: '1',
    color: '#2EC4B6',
    title: 'Direito Imobiliário',
  },
  {
    id: '2',
    color: '#5F6CAF',
    title: 'Assessoria Jurídica',
  },
  {
    id: '3',
    color: '#F4A261',
    title: 'Direito Público e Administrativo',
  },
  {
    id: '4',
    color: '#F4A261',
    title: 'Direito Público',
  },
  {
    id: '5',
    color: '#F4A261',
    title: 'Direito Administrativo',
  },
  {
    id: '6',
    color: '#F28482',
    title: 'Direito de Família',
  },
  {
    id: '7',
    color: '#E391ED',
    title: 'Direito Tributário',
  },
];

export function ArticleSection() {
  const [artigos, setArtigos] = useState<ArtigoLista[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const fetchArticles = async () => {
    try {
      const now = Date.now();
      if (ARTICLES_CACHE && now - ARTICLES_CACHE.timestamp < ARTICLES_CACHE_TTL_MS) {
        setArtigos(ARTICLES_CACHE.data);
        return;
      }

      const res = await fetch('/api/artigos');
      if (!res.ok) {
        const msg = `Falha na API: ${res.status} ${res.statusText}`;
        throw new Error(msg);
      }
      const data: ArtigoLista[] = await res.json();
      setArtigos(data);
      ARTICLES_CACHE = { data, timestamp: Date.now() };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      setArtigos([]);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const getAreaColor = (tag: string) => {
    const area = AREAS.find((a) => a.title === tag);
    return area ? area.color : '#6B7280';
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (artigos.length === 0 || error) {
    return null;
  }

  const artigosToShow = artigos.slice(0, 2);

  return (
    <section className="flex justify-center w-full bg-[#FFFBF5]">
      <div className="flex flex-col px-5 py-10 lg:px-20 lg:py-20 lg:max-w-7xl mx-auto w-full">
        <h2 className="text-[#192437] text-lg font-bold tracking-widest mb-6">NOSSOS ARTIGOS</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {artigosToShow
            .toSorted((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map((artigo) => (
              <Link
                key={artigo.slug}
                href={`/artigo/${artigo.slug}`}
                className="group bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 max-h-[320px] sm:max-h-[360px]"
              >
                <article className="h-full flex flex-col">
                  <div className="p-6 flex flex-col h-full">
                    <header>
                      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                        <p className="text-sm text-gray-500">{formatarData(artigo.date)}</p>
                        {artigo.tags && artigo.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {artigo.tags.map((tag) => (
                              <span
                                key={tag}
                                style={{ backgroundColor: getAreaColor(tag) }}
                                className="px-2 py-1 text-white text-xs font-bold rounded-md"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <h2 className="text-primary text-base sm:text-2xl font-bold mb-2">{artigo.title}</h2>
                    </header>

                    <div className="relative flex-1 overflow-hidden mb-2">
                      {artigo.description.split('\n').map((paragrafo, index) => (
                        <p key={index} className="text-primary text-sm mb-1">
                          {paragrafo}
                        </p>
                      ))}

                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 bottom-0 h-6 sm:h-16 bg-gradient-to-t from-white to-transparent"
                      />
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                      <span className="text-secondary sm:text-primary text-sm font-bold group-hover:text-secondary">
                        Ler mais →
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
        </div>

        <BannerMoreArticles />
      </div>
    </section>
  );
}